import type { TraceController_trace_v1Query } from '@repo/api-client';

import type { ReportingPeriod } from './reporting-period';

/**
 * What a trace is started from, and the query that asks for it.
 *
 * `GET /v1/reports/trace` is **two routes wearing one path**, and telling them
 * apart is the whole job of this module. Every parameter on it is optional in the
 * document, which reads as a permissive route and is the opposite of what it is:
 * exactly one entry point must be named, and which one is named decides whether
 * five *other* parameters are required, permitted or refused outright.
 *
 * ## The two families
 *
 * **An artefact** — a receipt number or a ticket number. The trace is anchored on
 * one printed thing and returns everything connected to it: the basket, its
 * lines, its refund, what they moved on a shelf, what they wrote to the books,
 * and what was audited against it. It is **not paged** (`limit` and `nextCursor`
 * are documented null on it) and it takes **no period** — the period is derived
 * from the days the subject's own timeline falls in.
 *
 * `from`, `to`, `outletId`, `cursor` and `limit` are **refused beside a subject**,
 * `400`, rather than ignored. The document gives the reason and it is a good one:
 * "a period that quietly excluded a refund a week later would produce an
 * incomplete answer that looks complete". So this module does not merely leave
 * them out when they are empty — it cannot include them at all, which is what
 * makes "a receipt number traces with no date range supplied" a property of the
 * type rather than of a screen remembering.
 *
 * **A person or an item** — `actorId` or `catalogueItemId`. This is a search
 * bounded by a period rather than an anchor on a record, so `from` and `to` are
 * **required**, `outletId` narrows it, and it **pages**.
 *
 * ## The cursor is opaque, and this module is the only thing that touches one
 *
 * It is passed through. Not parsed, not decoded, not compared, not constructed,
 * not defaulted to a synthesised first value. The route refuses "a cursor that is
 * not the shape this route issued" with a non-retryable `400`, so the only safe
 * thing to do with one is store what was given and hand it back byte for byte.
 * `cursor-pagination.ts` holds the trail; this holds the one line that puts it in
 * a query.
 *
 * Ascending on an append-only key is what makes that safe without freezing
 * anything: a till recording a sale mid-read appends *after* the reader's
 * position, so paging neither skips nor duplicates and there is nothing to warn
 * anybody about.
 *
 * ## Why `saleId` and `ticketId` are not modes
 *
 * The route takes both, and they address the same two artefacts this module
 * already reaches by their printed numbers. What they need is a uuid, which is
 * not something a person has in their hand or can be given a placeholder for —
 * `docs/ui-conventions.md` asks every field to show the shape of a good answer,
 * and there is no good answer to show for a field only a link could fill. If a
 * later ticket wants "Trace this sale" from a sale's own record, `saleId` is what
 * it should carry, and adding it here is a mode and a branch rather than a
 * rewrite.
 */

/** The four ways in this screen offers. */
export type TraceMode = 'RECEIPT_NUMBER' | 'TICKET_NUMBER' | 'STAFF' | 'CATALOGUE_ITEM';

/**
 * Anchored on one printed artefact: everything connected to it, unpaged, and no
 * period of its own.
 */
export const ARTEFACT_MODES = [
  'RECEIPT_NUMBER',
  'TICKET_NUMBER',
] as const satisfies readonly TraceMode[];

/** Bounded by a period: a search over a person or an item, paged by cursor. */
export const BOUNDED_MODES = ['STAFF', 'CATALOGUE_ITEM'] as const satisfies readonly TraceMode[];

/** Every mode, in the order the picker offers them. The receipt is first because it is the one on the counter. */
export const TRACE_MODES = [
  ...ARTEFACT_MODES,
  ...BOUNDED_MODES,
] as const satisfies readonly TraceMode[];

const BOUNDED = new Set<TraceMode>(BOUNDED_MODES);

/** Whether this mode is the period-bounded, paged half of the route. */
export const isBoundedMode = (mode: TraceMode): boolean => BOUNDED.has(mode);

const MODE_LABELS: Readonly<Record<TraceMode, string>> = {
  RECEIPT_NUMBER: 'Receipt number',
  TICKET_NUMBER: 'Ticket number',
  STAFF: 'Staff member',
  CATALOGUE_ITEM: 'Catalogue item',
};

export const labelForTraceMode = (mode: TraceMode): string => MODE_LABELS[mode];

/**
 * What the field under the picker is called and what a good answer looks like.
 *
 * Kept beside the labels so a mode cannot be added with one and not the other.
 */
const MODE_FIELD_LABELS: Readonly<Record<TraceMode, string>> = {
  RECEIPT_NUMBER: 'Receipt number',
  TICKET_NUMBER: 'Ticket number',
  STAFF: 'Who',
  CATALOGUE_ITEM: 'Which item',
};

export const labelForTraceField = (mode: TraceMode): string => MODE_FIELD_LABELS[mode];

/** What is being traced: a mode and the one value it needs. */
export interface TraceSubject {
  readonly mode: TraceMode;

  /**
   * The printed number as it was typed, or the id the picker chose. Empty means
   * nothing has been named yet, which is not a request.
   */
  readonly value: string;
}

/** Which query parameter each mode names. Exactly one is ever set. */
const ENTRY_POINTS: Readonly<
  Record<TraceMode, 'saleNumber' | 'ticketNumber' | 'actorId' | 'catalogueItemId'>
> = {
  RECEIPT_NUMBER: 'saleNumber',
  TICKET_NUMBER: 'ticketNumber',
  STAFF: 'actorId',
  CATALOGUE_ITEM: 'catalogueItemId',
};

/**
 * The query for one trace, or `null` where the screen has not been given enough
 * to ask a question with.
 *
 * `null` rather than a partly-filled query: a bounded trace missing a bound and a
 * subject that has not been typed yet are both "no request", and the screen shows
 * its own copy for that rather than sending something the API will refuse for a
 * reason it can already see.
 *
 * **`byDay` is always on.** The breakdown is derived by widening the aggregate's
 * grouping rather than by asking again per day, so it costs one further statement
 * whatever the period's length — there is no version of this screen where not
 * asking for it is the cheaper answer.
 *
 * The return type is the operation's own, from the generated client, so a
 * parameter renamed upstream fails `pnpm typecheck` here rather than 400-ing on a
 * till.
 */
export function traceQuery({
  subject,
  period,
  outletId,
  cursor,
}: {
  readonly subject: TraceSubject;
  readonly period: ReportingPeriod;

  /** Narrowing a bounded trace to one outlet, or `undefined` for every outlet in reach. */
  readonly outletId: string | undefined;

  /**
   * The cursor the current page was reached by, `null` on the first page.
   *
   * **Opaque.** Whatever arrives here was issued by this route and is passed
   * through unchanged; nothing in this module reads, splits, decodes or compares
   * one, and nothing constructs one.
   */
  readonly cursor: string | null;
}): TraceController_trace_v1Query | null {
  const value = subject.value.trim();

  if (value === '') return null;

  const entryPoint = ENTRY_POINTS[subject.mode];

  // An artefact bounds its own timeline. A range, an outlet, a cursor or a limit
  // beside it is refused 400 rather than ignored, so none of them is reachable
  // from this branch — not conditionally, not when they happen to be empty.
  if (!isBoundedMode(subject.mode)) {
    return { [entryPoint]: value, byDay: true };
  }

  if (period.from === '' || period.to === '') return null;

  return {
    [entryPoint]: value,
    from: period.from,
    to: period.to,
    byDay: true,
    // A filter narrows within reach and never widens it, so "every outlet" is the
    // field omitted rather than a value meaning all.
    ...(outletId === undefined ? {} : { outletId }),
    // Verbatim, and absent on the first page — the document's "omit it for the
    // first page", which is not the same as sending a null.
    ...(cursor === null ? {} : { cursor }),
  };
}
